import React, { useState } from 'react';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import {
	ProductApi,
	OrderProduct,
	PricingTableObject,
	ByVariant,
	PurchaseTypes,
} from 'common/types';
import { Theme, Typography, Divider, useTheme, Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import VariantCard from '../VariantCard';
import SelectedVariantRow from '../SelectedVariantRow';
import {
	createOrderProductFromProductApi,
	PartialOrderProductFields,
	OrderProductCreation,
	newFirestoreId,
} from 'common/utils/newRentalUtils';
import { useMediaQuery as isMediaQuery } from '@mui/material';
import AvailabilityStatus from 'components/AvailabilityStatus';
import { getLanguage } from 'services/localization/utils';
import { notNull } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import { getIncludedVariantIds } from './utils';
import { Dictionary, mergeWith, uniq, unzip } from 'lodash';
import { useTranslation } from 'services/localization/useTranslation';
import { getEndDateWithInventoryBlockers, getVariantName } from 'common/utils/productUtils';
import {
	AvailabilityRange,
	calculateAvailabilityForTimePeriod,
} from 'common/api/frontend/inventory_new/availabilityRange';
import {
	getProductVariants,
	getVariantIds,
	getVariantValueNames,
	hasMultipleVariants,
} from 'common/modules/products/variants';
import { BaseProductVariant } from 'common/modules/inventory';
import { getReservedVariantIdsCountFromSkuIds, getSkuIds } from 'common/modules/orders';
import { sumItems } from 'common/utils/arrays';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getDefaultTimezone } from 'common/utils/dateUtils';

interface OwnProps {
	open: boolean;
	orderProducts: OrderProduct[];
	allOrderProducts: OrderProduct[];
	onClose: () => void;
	stockProduct: ProductApi;
	partialOrderProductFields: PartialOrderProductFields;
	allStockProducts: ProductApi[];
	addProduct: (product: OrderProduct) => void;
	savedPricingTables?: PricingTableObject | undefined;
	removeProduct: (id: string) => void;
	startDate: string;
	endDate: string;
	availability: ByVariant<AvailabilityRange[]> | undefined;
	quantities: ByVariant<number | null> | undefined;
	fullAvailability: number | null | undefined;
	fullQuantity: number | null | undefined;
	availabilityNotLoadedForLongDuration: boolean;
	currencyCode: string;
	taxExcluded: boolean;
	isLoadingAvailability: boolean;
}

interface SelectedVariantAmounts {
	[variantId: string]: string[];
}

type Props = OwnProps & WithStyles<typeof styles>;

const getAlreadySelectedVariantsOrderProductIds = (orderProducts: OrderProduct[]) => {
	return orderProducts.reduce((tot, product) => {
		const variantIds = product.summary.variantIds;
		variantIds.forEach((variantId) => {
			tot[variantId] = [...(tot[variantId] ?? []), product.id];
		});
		return tot;
	}, {} as { [variantId: string]: string[] });
};

const VariantSelectorDialog = (props: Props) => {
	const {
		stockProduct,
		allStockProducts,
		partialOrderProductFields,
		classes,
		savedPricingTables,
		orderProducts,
		allOrderProducts,
		availabilityNotLoadedForLongDuration,
		currencyCode,
		taxExcluded,
	} = props;
	const theme = useTheme();
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone) ?? getDefaultTimezone();
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const [selectedVariants, setSelectedVariants] = useState<SelectedVariantAmounts>(
		getAlreadySelectedVariantsOrderProductIds(orderProducts),
	);
	const [deletedProducts, setDeletedProducts] = useState<string[]>([]);
	const [variantCardClicked, setVariantCardClicked] = useState(false);
	const isSetWithVariants = stockProduct.set && hasMultipleVariants(stockProduct);

	const setProductsWithVariants = (stockProduct.setProductIds ?? [])
		.map((id) => allStockProducts.find((p) => p.id === id))
		.filter((p): p is ProductApi => !!p && hasMultipleVariants(p));

	const { t } = useTranslation();

	const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
		props.endDate,
		stockProduct,
		allStockProducts,
	);

	const selectedOrderProductIds = orderProducts.map((p) => p.id);
	const otherOrderProductsFromCart = allOrderProducts.filter(
		(p) => !selectedOrderProductIds.includes(p.id),
	);

	const reservedVariantsCountsFromOtherProducts = getReservedVariantIdsCountFromSkuIds(
		getSkuIds(otherOrderProductsFromCart),
		allStockProducts,
	);

	const selectedVariantsCounts = Object.entries(selectedVariants).reduce(
		(tot, [variantId, productIds]) => {
			return {
				...tot,
				[variantId]: productIds.length,
			};
		},
		{} as Dictionary<number>,
	);

	const totalVariantsCounts = mergeWith(
		reservedVariantsCountsFromOtherProducts,
		selectedVariantsCounts,
		(val1, val2) => (isNaN(val1) ? 0 : val1) + (isNaN(val2) ? 0 : val2),
	);

	const onAddVariantClick = (variantId: string, byVariantCard?: boolean) => {
		setSelectedVariants((selectedVariants) => ({
			...selectedVariants,
			[variantId]: [...(selectedVariants[variantId] ?? []), newFirestoreId()],
		}));
		setVariantCardClicked(!!byVariantCard);
	};

	const onDeleteVariantRow = (variantId: string) => {
		const productsToDelete = selectedVariants[variantId];
		setSelectedVariants((selectedVariants) => {
			const { [variantId]: _delete, ...otherIds } = selectedVariants;
			return otherIds;
		});
		if (!!productsToDelete?.length) {
			setDeletedProducts((deletedProducts) => [...deletedProducts, ...productsToDelete]);
		}
	};

	const onDeleteVariant = (variantId: string) => {
		const updatedSelectedVariants = { ...selectedVariants };
		const productToDelete = updatedSelectedVariants[variantId].pop();
		if (updatedSelectedVariants[variantId].length < 1) {
			delete updatedSelectedVariants[variantId];
		}
		setSelectedVariants(updatedSelectedVariants);
		setVariantCardClicked(false);
		if (!!productToDelete) {
			setDeletedProducts((deletedProducts) => [...deletedProducts, productToDelete]);
		}
	};

	const getAmountOfVariantsSelected = (variant: BaseProductVariant) => {
		return totalVariantsCounts[variant.id] ?? 0;
	};

	const getMinAmountOfProductsAvailable = () => {
		const productsToCheck = setProductsWithVariants.length
			? setProductsWithVariants
			: [stockProduct];
		const productAvailabilityCounts = productsToCheck.map((p) => {
			const variantIds = getVariantIds(p);
			const maxVariantAvailability = variantIds.map((id) => {
				const variantAvailability = !props.availability
					? 0
					: calculateAvailabilityForTimePeriod(
							props.startDate,
							endDateWithInventoryBlockers,
							props.availability[id],
					  );
				const selectedCount = totalVariantsCounts[id] ?? 0;
				return variantAvailability === null ? null : variantAvailability - selectedCount;
			});
			if (maxVariantAvailability.some((a) => a === null)) return null;
			return sumItems(maxVariantAvailability.filter(notNull));
		});
		const nonNullProductAvailabilities = productAvailabilityCounts.filter(notNull);
		return !!nonNullProductAvailabilities.length ? Math.min(...nonNullProductAvailabilities) : null;
	};

	const renderProductHeader = () => {
		let totalAvailabilityForVariants = props.fullAvailability;
		if (props.fullAvailability != null) {
			const minProductAvailability = getMinAmountOfProductsAvailable();
			totalAvailabilityForVariants = minProductAvailability;
		}

		return (
			<Grid
				className={classes.productHeader}
				container
				spacing={0}
				alignItems="center"
				justifyContent="space-between"
			>
				<Grid item xs>
					<Typography variant="h6" className={classes.mobileHeader}>
						{getTranslation(props.stockProduct.name, getLanguage())}
					</Typography>
				</Grid>
				<Grid>
					<AvailabilityStatus
						availability={totalAvailabilityForVariants}
						quantity={props.fullQuantity}
						isLoadingAvailability={props.isLoadingAvailability}
						availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
					/>
				</Grid>
			</Grid>
		);
	};

	const getSelectedAmountsPerEachSetProduct = (
		setProductsWithVariants: ProductApi[],
		selectedVariants: SelectedVariantAmounts,
	) => {
		const possibleSetProductsVariantIds = setProductsWithVariants.map(getVariantIds);

		return possibleSetProductsVariantIds.map((productVariants) =>
			productVariants.reduce(
				(acc, val) => (selectedVariants[val] ? acc + selectedVariants[val].length : acc),
				0,
			),
		);
	};

	const hasSelectedEqualAmountOfSetProducts = (
		setProductsWithVariants: ProductApi[],
		selectedVariants: SelectedVariantAmounts,
	) => {
		const selectedAmountsPerEachSetProduct = getSelectedAmountsPerEachSetProduct(
			setProductsWithVariants,
			selectedVariants,
		);
		return uniq(selectedAmountsPerEachSetProduct).length === 1;
	};

	const renderDialogActions = () => {
		const selectedVariantsLength = Object.keys(selectedVariants).length;
		const noSelectedVariantsAndOrderProducts =
			selectedVariantsLength < 1 && orderProducts.length < 1;
		const doesntHaveEqualAmountOfSetProductsSelected =
			setProductsWithVariants.length > 0
				? selectedVariantsLength > 0 &&
				  !hasSelectedEqualAmountOfSetProducts(setProductsWithVariants, selectedVariants)
				: false;

		return (
			<div className={classes.dialogActionsContainer}>
				<Button
					className={classes.dialogActionBtn}
					size={isMediaQuery(theme.breakpoints.up('sm')) ? 'large' : 'medium'}
					color="secondary"
					onClick={props.onClose}
				>
					{t('common:actions.cancel')}
				</Button>
				<Button
					className={classes.dialogActionBtn}
					onClick={handleAddToOrder}
					variant="contained"
					color="primary"
					size={isMediaQuery(theme.breakpoints.up('sm')) ? 'large' : 'medium'}
					disabled={
						noSelectedVariantsAndOrderProducts || doesntHaveEqualAmountOfSetProductsSelected
					}
				>
					{orderProducts.length > 0
						? t('newRental.updateOrder', 'Update order')
						: t('newRental.addToOrder', 'Add to order')}
				</Button>
			</div>
		);
	};

	const getVariantIdsFromSingleVariantSetProducts = () => {
		if (!stockProduct || !stockProduct.set || !stockProduct.setProductIds) {
			return [];
		}
		return stockProduct.setProductIds
			.map((id) => {
				const match = allStockProducts.find((p) => p.id === id);
				return match && getProductVariants(match).length === 1
					? getProductVariants(match)[0].id
					: null;
			})
			.filter(notNull);
	};

	const handleAddOfSetProductsWithMultipleVariants = () => {
		const variantIdsOfSingleVartiantSetProducts = getVariantIdsFromSingleVariantSetProducts();
		const possibleSetProductsVariantIds = setProductsWithVariants.map(getVariantIds);

		const selectedVariantsFromEachSetProduct = possibleSetProductsVariantIds.map(
			(productVariantIds) => {
				return productVariantIds.reduce((acc: { [key: string]: string }[], variantId: string) => {
					if (!!selectedVariants[variantId]) {
						selectedVariants[variantId].forEach((id) => {
							acc.push({ [id]: variantId });
						});
					}
					return acc;
				}, []);
			},
		);

		const selectedVariantsAsBundeledSets = unzip(selectedVariantsFromEachSetProduct); //Note this is only a best guess of what each set comprises of.

		selectedVariantsAsBundeledSets.forEach((bundleVariantsArr) => {
			const bundleVariants: {
				[key: string]: string;
			} = Object.assign({}, ...bundleVariantsArr);
			if (
				orderProducts.some((product) =>
					Object.keys(bundleVariants).every((newProductId) => newProductId === product.id),
				)
			) {
				return;
			}
			if (Object.keys(bundleVariants).length < bundleVariantsArr.length) {
				return;
			}
			const selectedVariantIds: string[] = Object.values(bundleVariants);
			const productVariantIds = [...variantIdsOfSingleVartiantSetProducts, ...selectedVariantIds];
			const newPartialOrderProductFields: PartialOrderProductFields = {
				...partialOrderProductFields,
				id: Object.keys(bundleVariants)[0], //only one unique id is needed
			};

			const variant =
				stockProduct.variants.options.find((v) => productVariantIds.includes(v.id)) ??
				stockProduct.variants.options[0];

			const orderProductCreationObject: OrderProductCreation = {
				stockProduct,
				stockProductListForSetProducts: allStockProducts,
				savedPricingTables: savedPricingTables || {},
				partialOrderProductFields: newPartialOrderProductFields,
				segment: null,
				productVariantIds,
				selectedVariant: variant,
				currencyCode,
				taxExcluded,
				channel: 'ADMIN',
				purchaseType: PurchaseTypes.rental,
				timezone: shopTimezone,
				openingHours,
			};
			const orderProduct = createOrderProductFromProductApi(orderProductCreationObject);
			props.addProduct(orderProduct);
		});
	};

	const handleAddOfProducts = () => {
		Object.keys(selectedVariants).forEach((variantId) => {
			const variant =
				stockProduct.variants.options.find((productVariant) => productVariant.id === variantId) ??
				stockProduct.variants.options[0];
			selectedVariants[variantId].forEach((orderProductId) => {
				if (!orderProducts.find((orderProduct) => orderProduct.id === orderProductId)) {
					const productVariantIds = getIncludedVariantIds(
						stockProduct,
						allStockProducts,
						variantId,
					);
					const newPartialOrderProductFields: PartialOrderProductFields = {
						...partialOrderProductFields,
						id: orderProductId,
					};

					const orderProductCreationObject: OrderProductCreation = {
						stockProduct,
						stockProductListForSetProducts: allStockProducts,
						savedPricingTables: savedPricingTables || {},
						partialOrderProductFields: newPartialOrderProductFields,
						segment: null,
						selectedVariant: variant,
						currencyCode,
						taxExcluded,
						productVariantIds,
						channel: 'ADMIN',
						purchaseType: PurchaseTypes.rental,
						timezone: shopTimezone,
						openingHours,
					};

					const orderProduct = createOrderProductFromProductApi(orderProductCreationObject);
					props.addProduct(orderProduct);
				}
			});
		});
	};

	const handleAddToOrder = () => {
		setProductsWithVariants.length
			? handleAddOfSetProductsWithMultipleVariants()
			: handleAddOfProducts();

		if (deletedProducts.length > 0) {
			deletedProducts.forEach((productId) => {
				props.removeProduct(productId);
			});
		}
		props.onClose();
	};

	const renderSetVariantOptions = (product: ProductApi) => {
		return product.variants.options.map((variant) => {
			const variantAvailability = !props.availability
				? 0
				: calculateAvailabilityForTimePeriod(
						props.startDate,
						endDateWithInventoryBlockers,
						props.availability[variant.id],
				  );
			const selectedAtTheMoment = getAmountOfVariantsSelected(variant);
			const variantAvailabilityWithSelectedItems =
				variantAvailability || variantAvailability === 0
					? variantAvailability - selectedAtTheMoment
					: variantAvailability;

			return (
				<div key={variant.id}>
					<VariantCard
						variant={variant}
						onAddVariantClick={(id: string) => onAddVariantClick(id, true)}
						availability={variantAvailabilityWithSelectedItems}
						quantity={!props.quantities ? undefined : props.quantities[variant.id]}
						isSetWithVariants={false}
					/>
				</div>
			);
		});
	};

	const renderSelectedVariants = (product: ProductApi) => {
		const selectedVariantsIds = Object.keys(selectedVariants);
		const pVariants = product.variants.options.filter((v) => selectedVariantsIds.includes(v.id));

		if (pVariants.length === 0) {
			return null;
		} else {
			return pVariants.map((v, index) => {
				const { values: variantValues, id: variantId } = v;
				const variantName = getVariantName(getVariantValueNames(variantValues), getLanguage());
				const variantAvailability =
					isSetWithVariants || !props.availability
						? 0
						: calculateAvailabilityForTimePeriod(
								props.startDate,
								endDateWithInventoryBlockers,
								props.availability[variantId],
						  );

				const selectedAtTheMoment = getAmountOfVariantsSelected(v);
				const variantAvailabilityWithSelectedItems =
					variantAvailability || variantAvailability === 0
						? variantAvailability - selectedAtTheMoment
						: variantAvailability;

				return (
					<div key={variantId}>
						<Divider />
						<SelectedVariantRow
							variantName={variantName}
							variantId={variantId}
							unitAmount={selectedVariants[variantId].length}
							onDeleteRow={(id: string) => onDeleteVariantRow(id)}
							onAddVariant={(id: string) => onAddVariantClick(id)}
							onRemoveVariant={(id: string) => onDeleteVariant(id)}
							addedByVariantCard={variantCardClicked}
							variantAvailability={variantAvailabilityWithSelectedItems ?? undefined}
							rowNumber={index}
							isSetWithVariants={isSetWithVariants}
						/>
					</div>
				);
			});
		}
	};

	const renderProductVariantOptions = (productVariants: BaseProductVariant[]) => {
		if (productVariants.length > 1) {
			return productVariants.map((variant) => {
				const variantAvailability =
					isSetWithVariants || !props.availability
						? 0
						: calculateAvailabilityForTimePeriod(
								props.startDate,
								endDateWithInventoryBlockers,
								props.availability[variant.id],
						  );
				const selectedAtTheMoment = getAmountOfVariantsSelected(variant);
				const variantAvailabilityWithSelectedItems =
					variantAvailability || variantAvailability === 0
						? variantAvailability - selectedAtTheMoment
						: variantAvailability;

				return (
					<div key={variant.id}>
						<VariantCard
							variant={variant}
							onAddVariantClick={(id: string) => onAddVariantClick(id, true)}
							availability={variantAvailabilityWithSelectedItems}
							quantity={!props.quantities ? undefined : props.quantities[variant.id]}
							isSetWithVariants={isSetWithVariants}
						/>
					</div>
				);
			});
		} else {
			return null;
		}
	};

	const setProductVariants = (setProductsWithVariants: ProductApi[]) => {
		return setProductsWithVariants.map((product: ProductApi) => {
			return (
				<Typography key={product.id} variant={'h6'} align={'center'}>
					{`${t('newRental.chooseSetVariant', 'Choose variant')} ${getTranslation(
						product.name,
						getLanguage(),
					)}`}
					<div className={classes.container}>{renderSetVariantOptions(product)}</div>
					<div className={classes.selectedProductsContainer}>{renderSelectedVariants(product)}</div>
				</Typography>
			);
		});
	};

	const productVariants = (product: ProductApi) => {
		return (
			<Typography variant="h6" align="center">
				{`${t('newRental.chooseSetVariant', 'Choose variant')}`}
				<div className={classes.container}>
					{renderProductVariantOptions(product.variants.options)}
				</div>
				<div className={classes.selectedProductsContainer}>{renderSelectedVariants(product)}</div>
			</Typography>
		);
	};

	return (
		<Dialog
			onClose={props.onClose}
			disableEscapeKeyDown
			maxWidth="xs"
			aria-labelledby="confirmation-dialog-title"
			open={props.open}
			classes={{
				paper: classes.productDialogPaper,
			}}
		>
			<DialogContent
				classes={{
					root: classes.dialogContentRoot,
				}}
			>
				{renderProductHeader()}
				<div className={classes.variantSelectionContainer}>
					{setProductsWithVariants.length > 0
						? setProductVariants(setProductsWithVariants)
						: productVariants(stockProduct)}
				</div>
				{renderDialogActions()}
			</DialogContent>
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		dialogActionsContainer: {
			padding: theme.spacing(1.2, 2, 3, 2),
			display: 'flex',
			position: 'absolute',
			bottom: 0,
			width: '100%',
			justifyContent: 'center',
			backgroundColor: 'rgba(255, 255, 255, 0.8)',
		},
		dialogContentRoot: {
			padding: 0,
			'&:first-child': {
				paddingTop: 0,
			},
		},
		dialogActionBtn: {
			marginRight: 8,
		},
		productHeader: {
			padding: `${theme.spacing(1)} ${theme.spacing(2)} !important`,
			backgroundColor: `${theme.palette.background.secondary} !important`,
		},
		mobileHeader: {
			[theme.breakpoints.down('sm')]: {
				fontSize: '16px',
			},
		},
		variantSelectionContainer: {
			padding: '16px 4px 64px 4px !important',
			[theme.breakpoints.up('sm')]: {
				padding: `${theme.spacing(5)} ${theme.spacing(2)} !important`,
			},
		},
		productSelection: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: 8,
		},
		addButton: {
			padding: 8,
			flex: '0 0 auto',
			minWidth: 0,
		},
		addUnit: {
			flex: '1 0 auto',
			minWidth: 48,
		},
		productDialogPaper: {
			maxWidth: 'none',
			minWidth: '50%',
			[theme.breakpoints.down('md')]: {
				margin: '42px 16px',
			},
		},
		container: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			flexWrap: 'wrap',
			marginTop: 16,
		},

		selectedProductsContainer: {
			display: 'block',
			minHeight: '100px',
			[theme.breakpoints.down('md')]: {
				minHeight: '10px',
			},
			margin: '16px 0px',
			[theme.breakpoints.up('sm')]: {
				margin: '42px 24px',
			},
		},
	});

export default withStyles(styles)(VariantSelectorDialog);

/*  */
